<template>
  <main-template>
    <!-- Passo 1: escolher censo -->
    <template v-if="!censoSelecionado">
      <v-row class="ma-4 mb-0">
        <v-col>
          <h1 class="mb-0" style="line-height: 1.2">Preenchimento do Censo</h1>
          <div class="text-caption grey--text">
            Selecione o censo para iniciar o preenchimento
          </div>
        </v-col>
      </v-row>
      <v-row v-if="loading" class="ma-4 mt-0">
        <v-col><v-progress-linear indeterminate color="primary" /></v-col>
      </v-row>
      <v-row v-else class="ma-4 mt-0">
        <v-col cols="12">
          <div class="text-overline font-weight-bold mb-3 grey--text text--darken-1">
            CENSOS DISPONÍVEIS
          </div>
        </v-col>
        <v-col v-for="censo in censos" :key="censo.id" cols="12" sm="6" md="4">
          <v-card
            outlined
            class="rounded-xl pa-4"
            style="cursor: pointer"
            @click="selecionarCenso(censo)"
          >
            <div class="d-flex justify-space-between align-center mb-2">
              <v-chip x-small :color="censo.finalizado ? 'green' : 'orange'" dark>
                {{ censo.finalizado ? "Finalizado" : "Aberto" }}
              </v-chip>
              <span class="text-caption grey--text">{{
                censo.ano ? censo.ano.descricao : "—"
              }}</span>
            </div>
            <div class="text-subtitle-1 font-weight-bold mb-1">{{ censo.descricao }}</div>
            <div class="text-caption grey--text">
              {{ censo.etapas ? censo.etapas.length : 0 }} etapas
            </div>
            <div class="d-flex justify-end mt-3">
              <v-btn x-small text color="primary">
                Selecionar <v-icon x-small right>fa fa-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-col>
        <v-col v-if="censos.length === 0" cols="12">
          <div class="text-caption grey--text">Nenhum censo disponível.</div>
        </v-col>
      </v-row>
    </template>

    <!-- Passo 2: escolher etapa -->
    <template v-else-if="!etapaSelecionada">
      <v-row class="ma-4 mb-0">
        <v-col>
          <div class="d-flex align-center" style="gap: 8px">
            <v-btn icon x-small @click="censoSelecionado = null">
              <v-icon>fa fa-angle-left</v-icon>
            </v-btn>
            <div>
              <h1 class="mb-0" style="line-height: 1.2">
                {{ censoSelecionado.descricao }}
              </h1>
              <div class="text-caption grey--text">
                Selecione uma etapa para preencher
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="ma-4 mt-0">
        <v-col cols="12">
          <div class="text-overline font-weight-bold mb-3 grey--text text--darken-1">
            ETAPAS
          </div>
        </v-col>
        <v-col
          v-for="(etapa, index) in censoSelecionado.etapas"
          :key="etapa.id"
          cols="12"
          sm="6"
        >
          <v-card
            outlined
            class="rounded-xl pa-4"
            style="cursor: pointer"
            @click="selecionarEtapa(etapa)"
          >
            <div class="d-flex justify-space-between align-center mb-2">
              <v-chip
                x-small
                label
                color="primary"
                class="blue--text text--darken-2 font-weight-bold"
              >
                ETAPA {{ index + 1 }}
              </v-chip>
              <v-chip
                x-small
                :color="etapa.situacao === 'fechada' ? 'green' : 'orange'"
                dark
              >
                {{ etapa.situacao === "fechada" ? "Finalizada" : "Em coleta" }}
              </v-chip>
            </div>
            <div class="text-subtitle-1 font-weight-bold">{{ etapa.descricao }}</div>
            <div class="d-flex justify-end mt-3">
              <v-btn x-small text color="primary">
                Preencher <v-icon x-small right>fa fa-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-col>
        <v-col
          v-if="!censoSelecionado.etapas || censoSelecionado.etapas.length === 0"
          cols="12"
        >
          <div class="text-caption grey--text">Nenhuma etapa disponível.</div>
        </v-col>
      </v-row>
    </template>

    <!-- Passo 3: preencher — layout com tabs no topo colorido -->
    <template v-else>
      <v-row class="ma-4">
        <v-col cols="12">
          <div v-if="loadingEtapa">
            <v-progress-linear indeterminate color="primary" />
          </div>
          <v-card v-else-if="etapaDetalhada" outlined>
            <v-tabs
              v-model="moduloAtivo"
              background-color="primary"
              dark
              centered
              icons-and-text
              show-arrows
            >
              <v-tab v-for="modulo in etapaDetalhada.modulos" :key="modulo.id">
                {{ modulo.descricao }}
                <v-icon>fa fa-list</v-icon>
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="moduloAtivo" style="overflow-y: auto; max-height: calc(100vh - 220px)">
              <v-tab-item v-for="modulo in etapaDetalhada.modulos" :key="modulo.id">
                <!-- Módulo 20: turmas -->
                <template v-if="isTurmaModulo(modulo)">
                  <v-tabs
                    v-model="turmaAtiva"
                    background-color="blue-grey lighten-5"
                    color="primary"
                    show-arrows
                    class="elevation-0"
                  >
                    <v-tab v-for="turma in turmas" :key="turma.id" class="text-caption">
                      {{ turma.descricao }}
                    </v-tab>
                  </v-tabs>
                  <v-divider />
                  <v-tabs-items v-model="turmaAtiva">
                    <v-tab-item v-for="turma in turmas" :key="turma.id">
                      <v-card flat class="pa-4">
                        <template v-if="modulo.campos && modulo.campos.length > 0">
                          <ValidationObserver v-slot="{ handleSubmit }">
                            <v-card
                              v-for="grupo in camposPorGrupo(modulo.campos)"
                              :key="grupo.nome"
                              outlined
                              class="mb-4"
                            >
                              <v-card-subtitle class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase">
                                {{ grupo.nome }}
                              </v-card-subtitle>
                              <v-card-text class="pt-1 pb-2">
                                <v-row>
                                  <v-col
                                    v-for="campo in grupo.campos"
                                    :key="campo.id"
                                    cols="12"
                                    sm="6"
                                    class="py-1"
                                  >
                                    <ValidationProvider
                                      :name="campo.nome"
                                      :rules="regrasCampo(campo)"
                                      v-slot="{ errors }"
                                    >
                                      <v-textarea
                                        v-if="campo.tipo === 'textarea'"
                                        v-model="respostas[turma.id][campo.id]"
                                        :label="campo.nome"
                                        :placeholder="campo.placeholder || ''"
                                        :error-messages="errors.length ? errors : campo.erros || []"
                                        outlined
                                        dense
                                        rows="3"
                                        auto-grow
                                      />
                                      <v-checkbox
                                        v-else-if="campo.tipo === 'checkbox'"
                                        v-model="respostas[turma.id][campo.id]"
                                        :label="campo.nome"
                                        :error-messages="errors.length ? errors : campo.erros || []"
                                        dense
                                        class="mt-0"
                                      />
                                      <v-select
                                        v-else-if="campo.tipo === 'select'"
                                        v-model="respostas[turma.id][campo.id]"
                                        :label="campo.nome"
                                        :items="campo.opcoes || []"
                                        :placeholder="campo.placeholder || ''"
                                        :error-messages="errors.length ? errors : campo.erros || []"
                                        outlined
                                        dense
                                      />
                                      <v-text-field
                                        v-else
                                        v-model="respostas[turma.id][campo.id]"
                                        :label="campo.nome"
                                        :placeholder="campo.placeholder || ''"
                                        :type="
                                          campo.tipo === 'number'
                                            ? 'number'
                                            : campo.tipo === 'date'
                                            ? 'date'
                                            : 'text'
                                        "
                                        :error-messages="errors.length ? errors : campo.erros || []"
                                        outlined
                                        dense
                                      />
                                    </ValidationProvider>
                                  </v-col>
                                </v-row>
                              </v-card-text>
                            </v-card>
                            <div class="d-flex justify-end mt-2">
                              <v-btn
                                color="primary"
                                :loading="salvandoModulo === `${modulo.id}-${turma.id}`"
                                @click="handleSubmit(() => salvarRespostas(modulo, turma))"
                              >
                                <v-icon left small>fa fa-save</v-icon>
                                Salvar turma
                              </v-btn>
                            </div>
                          </ValidationObserver>
                        </template>
                        <div v-else class="text-caption grey--text py-2 text-center">
                          Nenhum campo configurado neste módulo.
                        </div>
                      </v-card>
                    </v-tab-item>
                  </v-tabs-items>
                </template>

                <!-- Módulo 30: pessoas físicas -->
                <template v-else-if="isPessoaFisicaModulo(modulo)">
                  <div class="d-flex justify-end align-center px-4 py-2">
                    <v-radio-group
                      v-model="pessoaFisicaTipo"
                      row
                      dense
                      hide-details
                      class="mt-2 MB-1"
                      @change="trocarTipoPessoa"
                    >
                      <v-radio label="Professor" value="professor" />
                      <v-radio label="Aluno" value="aluno" />
                      <v-radio label="Gestor" value="gestor" />
                    </v-radio-group>
                  </div>
                  <v-divider />
                  <div v-if="pessoasAtuais.length === 0" class="text-caption grey--text py-4 text-center">
                    Nenhuma pessoa encontrada.
                  </div>
                  <template v-else>
                    <div class="pa-4">
                      <v-card
                        v-for="pessoa in pessoasPaginadas"
                        :key="`${pessoaFisicaTipo}-${pessoa.id}`"
                        outlined
                        class="mb-4"
                      >
                        <!-- <v-card-title class="subtitle-2 py-2 px-4 blue-grey lighten-5">
                          {{ pessoaNome(pessoa) }}
                        </v-card-title> -->
                        <v-divider />
                        <v-tabs
                          v-model="subTabsPessoas[`${pessoaFisicaTipo}_${pessoa.id}`]"
                          background-color="primary"
                          dark
                          show-arrows
                          class="elevation-0 mb-6"
                        >
                          <v-tab
                            v-for="grupo in pessoaFisicaSubTabs(modulo.campos)"
                            :key="grupo.nome"
                            class="text-caption"
                          >
                            {{ grupo.nome }}
                          </v-tab>
                        </v-tabs>
                        <ValidationObserver :key="`obs-${pessoaFisicaTipo}-${pessoa.id}`" v-slot="{ handleSubmit }">
                          <v-tabs-items v-model="subTabsPessoas[`${pessoaFisicaTipo}_${pessoa.id}`]">
                            <v-tab-item
                              v-for="grupo in pessoaFisicaSubTabs(modulo.campos)"
                              :key="grupo.nome"
                            >
                              <v-card-text>
                                <v-row>
                                  <v-col
                                    v-for="campo in grupo.campos"
                                    :key="campo.id"
                                    cols="12"
                                    sm="6"
                                    class="py-1"
                                  >
                                    <ValidationProvider
                                      :name="campo.nome"
                                      :rules="regrasCampo(campo)"
                                      v-slot="{ errors }"
                                    >
                                      <v-textarea
                                        v-if="campo.tipo === 'textarea'"
                                        v-model="respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`][campo.id]"
                                        :label="campo.nome"
                                        :placeholder="campo.placeholder || ''"
                                        :error-messages="errors.length ? errors : campo.erros || []"
                                        outlined
                                        dense
                                        rows="3"
                                        auto-grow
                                      />
                                      <v-checkbox
                                        v-else-if="campo.tipo === 'checkbox'"
                                        v-model="respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`][campo.id]"
                                        :label="campo.nome"
                                        :error-messages="errors.length ? errors : campo.erros || []"
                                        dense
                                        class="mt-0"
                                      />
                                      <v-select
                                        v-else-if="campo.tipo === 'select'"
                                        v-model="respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`][campo.id]"
                                        :label="campo.nome"
                                        :items="campo.opcoes || []"
                                        :placeholder="campo.placeholder || ''"
                                        :error-messages="errors.length ? errors : campo.erros || []"
                                        outlined
                                        dense
                                      />
                                      <v-text-field
                                        v-else
                                        v-model="respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`][campo.id]"
                                        :label="campo.nome"
                                        :placeholder="campo.placeholder || ''"
                                        :type="
                                          campo.tipo === 'number'
                                            ? 'number'
                                            : campo.tipo === 'date'
                                            ? 'date'
                                            : 'text'
                                        "
                                        :error-messages="errors.length ? errors : campo.erros || []"
                                        outlined
                                        dense
                                      />
                                    </ValidationProvider>
                                  </v-col>
                                </v-row>
                              </v-card-text>
                            </v-tab-item>
                          </v-tabs-items>
                          <!-- <v-card-actions class="justify-end">
                            <v-btn
                              color="primary"
                              small
                              :loading="salvandoModulo === `${modulo.id}-${pessoaFisicaTipo}-${pessoa.id}`"
                              @click="handleSubmit(() => salvarRespostas(modulo, null, null, pessoa))"
                            >
                              <v-icon left small>fa fa-save</v-icon>
                              Salvar
                            </v-btn>
                          </v-card-actions> -->
                        </ValidationObserver>
                      </v-card>
                    </div>
                    <div class="d-flex justify-center pb-4">
                      <v-pagination
                        v-model="pessoaPaginaAtual"
                        :length="Math.ceil(pessoasAtuais.length / pessoasPorPagina)"
                        total-visible="7"
                      />
                    </div>
                  </template>
                </template>

                <!-- Módulo 60: alunos (paginado) -->
                <template v-else-if="isAlunoModulo(modulo)">
                  <div v-if="alunos.length === 0" class="text-caption grey--text py-4 text-center">
                    Nenhum aluno encontrado.
                  </div>
                  <template v-else>
                    <div class="d-flex align-center justify-space-between px-4 py-3 blue-grey lighten-5">
                      <v-btn
                        icon
                        small
                        :disabled="alunoAtivo === 0"
                        @click="alunoAtivo--"
                      >
                        <v-icon small>fa fa-angle-left</v-icon>
                      </v-btn>
                      <div class="text-center">
                        <div class="text-subtitle-2 font-weight-bold">
                          {{ alunos[alunoAtivo].nomecompleto }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ alunoAtivo + 1 }} de {{ alunos.length }}
                        </div>
                      </div>
                      <v-btn
                        icon
                        small
                        :disabled="alunoAtivo === alunos.length - 1"
                        @click="alunoAtivo++"
                      >
                        <v-icon small>fa fa-angle-right</v-icon>
                      </v-btn>
                    </div>
                    <v-divider />

                    <!-- campos do aluno atual -->
                    <v-card flat class="pa-4">
                      <template v-if="modulo.campos && modulo.campos.length > 0">
                        <ValidationObserver :key="alunos[alunoAtivo].id" v-slot="{ handleSubmit }">
                          <v-card
                            v-for="grupo in camposPorGrupo(modulo.campos)"
                            :key="grupo.nome"
                            outlined
                            class="mb-4"
                          >
                            <v-card-subtitle class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase">
                              {{ grupo.nome }}
                            </v-card-subtitle>
                            <v-card-text class="pt-1 pb-2">
                              <v-row>
                                <v-col
                                  v-for="campo in grupo.campos"
                                  :key="campo.id"
                                  cols="12"
                                  sm="6"
                                  class="py-1"
                                >
                                  <ValidationProvider
                                    :name="campo.nome"
                                    :rules="regrasCampo(campo)"
                                    v-slot="{ errors }"
                                  >
                                    <v-textarea
                                      v-if="campo.tipo === 'textarea'"
                                      v-model="respostasAlunos[alunos[alunoAtivo].id][campo.id]"
                                      :label="campo.nome"
                                      :placeholder="campo.placeholder || ''"
                                      :error-messages="errors.length ? errors : campo.erros || []"
                                      outlined
                                      dense
                                      rows="3"
                                      auto-grow
                                    />
                                    <v-checkbox
                                      v-else-if="campo.tipo === 'checkbox'"
                                      v-model="respostasAlunos[alunos[alunoAtivo].id][campo.id]"
                                      :label="campo.nome"
                                      :error-messages="errors.length ? errors : campo.erros || []"
                                      dense
                                      class="mt-0"
                                    />
                                    <v-select
                                      v-else-if="campo.tipo === 'select'"
                                      v-model="respostasAlunos[alunos[alunoAtivo].id][campo.id]"
                                      :label="campo.nome"
                                      :items="campo.opcoes || []"
                                      :placeholder="campo.placeholder || ''"
                                      :error-messages="errors.length ? errors : campo.erros || []"
                                      outlined
                                      dense
                                    />
                                    <v-text-field
                                      v-else
                                      v-model="respostasAlunos[alunos[alunoAtivo].id][campo.id]"
                                      :label="campo.nome"
                                      :placeholder="campo.placeholder || ''"
                                      :type="
                                        campo.tipo === 'number'
                                          ? 'number'
                                          : campo.tipo === 'date'
                                          ? 'date'
                                          : 'text'
                                      "
                                      :error-messages="errors.length ? errors : campo.erros || []"
                                      outlined
                                      dense
                                    />
                                  </ValidationProvider>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                          <div class="d-flex justify-end mt-2">
                            <v-btn
                              color="primary"
                              :loading="salvandoModulo === `${modulo.id}-aluno-${alunos[alunoAtivo].id}`"
                              @click="handleSubmit(() => salvarRespostas(modulo, null, alunos[alunoAtivo]))"
                            >
                              <v-icon left small>fa fa-save</v-icon>
                              Salvar aluno
                            </v-btn>
                          </div>
                        </ValidationObserver>
                      </template>
                      <div v-else class="text-caption grey--text py-2 text-center">
                        Nenhum campo configurado neste módulo.
                      </div>
                    </v-card>
                  </template>
                </template>

                <!-- Módulo 40: gestores -->
                <template v-else-if="isGestorModulo(modulo)">
                  <div v-if="gestores.length === 0" class="text-caption grey--text py-4 text-center">
                    Nenhum gestor encontrado.
                  </div>
                  <template v-else>
                    <div class="d-flex align-center justify-space-between px-4 py-3 blue-grey lighten-5">
                      <v-btn
                        icon
                        small
                        :disabled="gestorAtivo === 0"
                        @click="gestorAtivo--"
                      >
                        <v-icon small>fa fa-angle-left</v-icon>
                      </v-btn>
                      <div class="text-center">
                        <div class="text-subtitle-2 font-weight-bold">
                          {{ gestores[gestorAtivo].nome }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ gestorAtivo + 1 }} de {{ gestores.length }}
                        </div>
                      </div>
                      <v-btn
                        icon
                        small
                        :disabled="gestorAtivo === gestores.length - 1"
                        @click="gestorAtivo++"
                      >
                        <v-icon small>fa fa-angle-right</v-icon>
                      </v-btn>
                    </div>
                    <v-divider />
                    <v-card flat class="pa-4">
                      <template v-if="modulo.campos && modulo.campos.length > 0">
                        <ValidationObserver :key="gestores[gestorAtivo].id" v-slot="{ handleSubmit }">
                          <v-card
                            v-for="grupo in camposPorGrupo(modulo.campos)"
                            :key="grupo.nome"
                            outlined
                            class="mb-4"
                          >
                            <v-card-subtitle class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase">
                              {{ grupo.nome }}
                            </v-card-subtitle>
                            <v-card-text class="pt-1 pb-2">
                              <v-row>
                                <v-col
                                  v-for="campo in grupo.campos"
                                  :key="campo.id"
                                  cols="12"
                                  sm="6"
                                  class="py-1"
                                >
                                  <ValidationProvider
                                    :name="campo.nome"
                                    :rules="regrasCampo(campo)"
                                    v-slot="{ errors }"
                                  >
                                    <v-textarea
                                      v-if="campo.tipo === 'textarea'"
                                      v-model="respostasGestores[gestores[gestorAtivo].id][campo.id]"
                                      :label="campo.nome"
                                      :placeholder="campo.placeholder || ''"
                                      :error-messages="errors.length ? errors : campo.erros || []"
                                      outlined
                                      dense
                                      rows="3"
                                      auto-grow
                                    />
                                    <v-checkbox
                                      v-else-if="campo.tipo === 'checkbox'"
                                      v-model="respostasGestores[gestores[gestorAtivo].id][campo.id]"
                                      :label="campo.nome"
                                      :error-messages="errors.length ? errors : campo.erros || []"
                                      dense
                                      class="mt-0"
                                    />
                                    <v-select
                                      v-else-if="campo.tipo === 'select'"
                                      v-model="respostasGestores[gestores[gestorAtivo].id][campo.id]"
                                      :label="campo.nome"
                                      :items="campo.opcoes || []"
                                      :placeholder="campo.placeholder || ''"
                                      :error-messages="errors.length ? errors : campo.erros || []"
                                      outlined
                                      dense
                                    />
                                    <v-text-field
                                      v-else
                                      v-model="respostasGestores[gestores[gestorAtivo].id][campo.id]"
                                      :label="campo.nome"
                                      :placeholder="campo.placeholder || ''"
                                      :type="campo.tipo === 'number' ? 'number' : campo.tipo === 'date' ? 'date' : 'text'"
                                      :error-messages="errors.length ? errors : campo.erros || []"
                                      outlined
                                      dense
                                    />
                                  </ValidationProvider>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                          <div class="d-flex justify-end mt-2">
                            <v-btn
                              color="primary"
                              :loading="salvandoModulo === `${modulo.id}-gestor-${gestores[gestorAtivo].id}`"
                              @click="handleSubmit(() => salvarRespostas(modulo, null, null, null, gestores[gestorAtivo]))"
                            >
                              <v-icon left small>fa fa-save</v-icon>
                              Salvar gestor
                            </v-btn>
                          </div>
                        </ValidationObserver>
                      </template>
                      <div v-else class="text-caption grey--text py-2 text-center">
                        Nenhum campo configurado neste módulo.
                      </div>
                    </v-card>
                  </template>
                </template>

                <!-- Módulos normais -->
                <v-card v-else flat class="pa-4">
                  <template v-if="modulo.campos && modulo.campos.length > 0">
                    <ValidationObserver v-slot="{ handleSubmit }">
                      <v-card
                        v-for="grupo in camposPorGrupo(modulo.campos)"
                        :key="grupo.nome"
                        outlined
                        class="mb-4"
                      >
                        <v-card-subtitle class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase">
                          {{ grupo.nome }}
                        </v-card-subtitle>
                        <v-card-text class="pt-1 pb-2">
                          <v-row dense>
                            <v-col
                              v-for="campo in grupo.campos"
                              :key="campo.id"
                              cols="12"
                              sm="6"
                              class="py-1"
                            >
                              <ValidationProvider
                                :name="campo.nome"
                                :rules="regrasCampo(campo)"
                                v-slot="{ errors }"
                              >
                                <v-textarea
                                  v-if="campo.tipo === 'textarea'"
                                  v-model="respostas[campo.id]"
                                  :label="campo.nome"
                                  :placeholder="campo.placeholder || ''"
                                  :error-messages="errors.length ? errors : campo.erros || []"
                                  outlined
                                  dense
                                  rows="3"
                                  auto-grow
                                />
                                <v-checkbox
                                  v-else-if="campo.tipo === 'checkbox'"
                                  v-model="respostas[campo.id]"
                                  :label="campo.nome"
                                  :error-messages="errors.length ? errors : campo.erros || []"
                                  dense
                                  class="mt-0"
                                />
                                <v-select
                                  v-else-if="campo.tipo === 'select'"
                                  v-model="respostas[campo.id]"
                                  :label="campo.nome"
                                  :items="campo.opcoes || []"
                                  :placeholder="campo.placeholder || ''"
                                  :error-messages="errors.length ? errors : campo.erros || []"
                                  outlined
                                  dense
                                />
                                <v-text-field
                                  v-else
                                  v-model="respostas[campo.id]"
                                  :label="campo.nome"
                                  :placeholder="campo.placeholder || ''"
                                  :type="
                                    campo.tipo === 'number'
                                      ? 'number'
                                      : campo.tipo === 'date'
                                      ? 'date'
                                      : 'text'
                                  "
                                  :error-messages="errors.length ? errors : campo.erros || []"
                                  outlined
                                  dense
                                />
                              </ValidationProvider>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                      <div class="d-flex justify-end mt-2">
                        <v-btn
                          color="primary"
                          :loading="salvandoModulo === modulo.id"
                          @click="handleSubmit(() => salvarRespostas(modulo))"
                        >
                          <v-icon left small>fa fa-save</v-icon>
                          Salvar
                        </v-btn>
                      </div>
                    </ValidationObserver>
                  </template>
                  <div v-else class="text-caption grey--text py-2 text-center">
                    Nenhum campo configurado neste módulo.
                  </div>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </main-template>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      censos: [],
      censoSelecionado: null,
      etapaSelecionada: null,
      etapaDetalhada: null,
      loadingEtapa: false,
      moduloAtivo: 0,
      respostas: {},
      salvandoModulo: null,
      turmas: [],
      turmaAtiva: 0,
      alunos: [],
      alunoAtivo: 0,
      respostasAlunos: {},
      gestores: [],
      gestorAtivo: 0,
      respostasGestores: {},
      professores: [],
      pessoaFisicaTipo: 'professor',
      pessoaPaginaAtual: 1,
      pessoasPorPagina: 5,
      subTabsPessoas: {},
      respostasPessoas: {},
    };
  },

  computed: {
    pessoasAtuais() {
      if (this.pessoaFisicaTipo === 'professor') return this.professores;
      if (this.pessoaFisicaTipo === 'aluno') return this.alunos;
      if (this.pessoaFisicaTipo === 'gestor') return this.gestores;
      return [];
    },
    pessoasPaginadas() {
      const inicio = (this.pessoaPaginaAtual - 1) * this.pessoasPorPagina;
      return this.pessoasAtuais.slice(inicio, inicio + this.pessoasPorPagina);
    },
  },

  async created() {
    await this.buscarCensos();
  },

  methods: {
    isRequired(campo) {
      return (campo?.validacoes || []).some((v) => v?.regra?.code === "required");
    },

    regrasCampo(campo) {
      const regras = {};

      campo?.validacoes.forEach((v) => {
        const code = v?.regra?.code;
        const valor = v?.valor;

        switch (code) {
          case "required":
            regras.required = true;
            break;
          case "max_length":
            regras.max = valor;
            break;
          case "min_length":
            regras.min = valor;
            break;
          case "fixed_length":
            if (campo.tipo !== "select") regras.length = valor;
            break;
          case "numeric":
            regras.numeric = true;
            break;
          case "email":
            regras.email = true;
            break;
          case "time":
            regras.timeValidator = true;
            break;
          case "in":
            regras.oneOf = valor?.split(",");
            break;
        }
      });

      return regras;
    },

    async buscarCensos() {
      try {
        this.censos = await this.$services.censoService.index();
      } catch {
        this.$toast.danger("Erro ao carregar censos.");
      } finally {
        this.loading = false;
      }
    },

    selecionarCenso(censo) {
      this.censoSelecionado = censo;
      this.etapaSelecionada = null;
      this.etapaDetalhada = null;
    },

    async trocarTipoPessoa() {
      this.pessoaPaginaAtual = 1;
      this.$loaderService.open("Carregando pessoas...");
      await this.$nextTick();
      this.$loaderService.close();
    },

    isTurmaModulo(modulo) {
      return Number(modulo.codigo) === 20;
    },

    isAlunoModulo(modulo) {
      return Number(modulo.codigo) === 60;
    },

    isPessoaFisicaModulo(modulo) {
      return Number(modulo.codigo) === 30;
    },

    isGestorModulo(modulo) {
      return Number(modulo.codigo) === 40;
    },

    pessoaNome(pessoa) {
      return pessoa.nome || pessoa.nomecompleto || '—';
    },

    pessoaFisicaSubTabs(campos) {
      const faixas = [
        { nome: 'Identificação', min: 1, max: 6 },
        { nome: 'Dados Pessoais', min: 7, max: 16 },
        { nome: 'Deficiências', min: 17, max: 35 },
        { nome: 'Recursos', min: 36, max: 49 },
        { nome: 'Formação', min: 50, max: 999 },
      ];
      return faixas
        .map((f) => ({
          nome: f.nome,
          campos: (campos || []).filter((c) => c.ordem >= f.min && c.ordem <= f.max),
        }))
        .filter((g) => g.campos.length > 0);
    },

    camposPorGrupo(campos) {
      const grupos = [];
      (campos || []).forEach((campo) => {
        const nome = campo.grupo || 'Geral';
        let grupo = grupos.find((g) => g.nome === nome);
        if (!grupo) {
          grupo = { nome, campos: [] };
          grupos.push(grupo);
        }
        grupo.campos.push(campo);
      });
      return grupos;
    },

    async selecionarEtapa(etapa) {
      this.etapaSelecionada = etapa;
      this.moduloAtivo = 0;
      this.turmaAtiva = 0;
      this.alunoAtivo = 0;
      this.gestorAtivo = 0;
      this.pessoaFisicaTipo = 'professor';
      this.pessoaPaginaAtual = 1;
      this.subTabsPessoas = {};
      this.respostas = {};
      this.respostasAlunos = {};
      this.respostasGestores = {};
      this.respostasPessoas = {};
      this.turmas = [];
      this.alunos = [];
      this.gestores = [];
      this.professores = [];
      this.loadingEtapa = true;
      this.etapaDetalhada = null;
      try {
        const [etapaDetalhada, turmas] = await Promise.all([
          this.$services.censoService.showEtapa(etapa.id),
          this.$services.turmasService.syncAll(),
        ]);
        this.etapaDetalhada = etapaDetalhada;
        this.turmas = turmas;
        this.alunos = etapaDetalhada.alunos_censo || [];
        this.professores = etapaDetalhada.professores_censo || [];
        this.gestores = etapaDetalhada.gestores_censo || [];

        (this.etapaDetalhada.modulos || []).forEach((modulo) => {
          if (this.isTurmaModulo(modulo)) {
            turmas.forEach((turma) => {
              this.$set(this.respostas, turma.id, {});
              (modulo.campos || []).forEach((campo) => {
                const valor = campo.valor_padrao_turmas?.[turma.id] ?? campo.valor_padrao ?? null;
                if (valor !== null && valor !== undefined) {
                  this.$set(this.respostas[turma.id], campo.id, valor);
                }
              });
            });
          } else if (this.isAlunoModulo(modulo)) {
            this.alunos.forEach((aluno) => {
              this.$set(this.respostasAlunos, aluno.id, {});
              (modulo.campos || []).forEach((campo) => {
                const valor = campo.valor_padrao_alunos?.[aluno.id] ?? campo.valor_padrao ?? null;
                if (valor !== null && valor !== undefined) {
                  this.$set(this.respostasAlunos[aluno.id], campo.id, valor);
                }
              });
            });
          } else if (this.isPessoaFisicaModulo(modulo)) {
            this.professores.forEach((p) => {
              this.$set(this.respostasPessoas, `professor_${p.id}`, {});
              this.$set(this.subTabsPessoas, `professor_${p.id}`, 0);
              (modulo.campos || []).forEach((campo) => {
                const vpp = campo.valor_padrao_pessoas?.professor;
                const valor = vpp?.[p.id] ?? campo.valor_padrao ?? null;

                if (valor !== null && valor !== undefined) {
                  this.$set(this.respostasPessoas[`professor_${p.id}`], campo.id, valor);
                }
              });
            });
            this.alunos.forEach((a) => {
              this.$set(this.respostasPessoas, `aluno_${a.id}`, {});
              this.$set(this.subTabsPessoas, `aluno_${a.id}`, 0);
              (modulo.campos || []).forEach((campo) => {
                const valor = campo.valor_padrao_pessoas?.aluno?.[a.id] ?? campo.valor_padrao ?? null;
                if (valor !== null && valor !== undefined) {
                  this.$set(this.respostasPessoas[`aluno_${a.id}`], campo.id, valor);
                }
              });
            });
            this.gestores.forEach((g) => {
              this.$set(this.respostasPessoas, `gestor_${g.id}`, {});
              this.$set(this.subTabsPessoas, `gestor_${g.id}`, 0);
              (modulo.campos || []).forEach((campo) => {
                const valor = campo.valor_padrao_pessoas?.gestor?.[g.id] ?? campo.valor_padrao ?? null;
                if (valor !== null && valor !== undefined) {
                  this.$set(this.respostasPessoas[`gestor_${g.id}`], campo.id, valor);
                }
              });
            });
          } else if (this.isGestorModulo(modulo)) {
            this.gestores.forEach((g) => {
              this.$set(this.respostasGestores, g.id, {});
              (modulo.campos || []).forEach((campo) => {
                const valor = campo.valor_padrao_gestores?.[g.id] ?? campo.valor_padrao ?? null;
                if (valor !== null && valor !== undefined) {
                  this.$set(this.respostasGestores[g.id], campo.id, valor);
                }
              });
            });
          } else {
            (modulo.campos || []).forEach((campo) => {
              if (campo.valor_padrao !== null && campo.valor_padrao !== undefined) {
                this.$set(this.respostas, campo.id, campo.valor_padrao);
              }
            });
          }
        });
      } catch {
        this.$toast.danger("Erro ao carregar etapa.");
      } finally {
        this.loadingEtapa = false;
      }
    },

    async salvarRespostas(modulo, turma = null, aluno = null, pessoa = null, gestor = null) {
      const key = turma
        ? `${modulo.id}-turma-${turma.id}`
        : aluno
        ? `${modulo.id}-aluno-${aluno.id}`
        : pessoa
        ? `${modulo.id}-${this.pessoaFisicaTipo}-${pessoa.id}`
        : gestor
        ? `${modulo.id}-gestor-${gestor.id}`
        : modulo.id;
      this.salvandoModulo = key;

      try {
        let payload;

        if (turma) {
          payload = { turma_id: turma.id, respostas: this.respostas[turma.id] || {} };
        } else if (aluno) {
          payload = { aluno_id: aluno.id, respostas: this.respostasAlunos[aluno.id] || {} };
        } else if (pessoa) {
          const pessoaKey = `${this.pessoaFisicaTipo}_${pessoa.id}`;
          payload = {
            pessoa_tipo: this.pessoaFisicaTipo,
            pessoa_id: pessoa.id,
            respostas: this.respostasPessoas[pessoaKey] || {},
          };
        } else if (gestor) {
          payload = {
            gestor_id: gestor.id,
            respostas: this.respostasGestores[gestor.id] || {},
          };
        } else {
          payload = { respostas: this.respostas };
        }

        await this.$services.censoService.salvarModulo(modulo.id, payload);
        this.$toast.success("Salvo com sucesso.");
      } catch {
        this.$toast.danger("Erro ao salvar.");
      } finally {
        this.salvandoModulo = null;
      }
    },
  }
};
</script>

